/**
 * useWorkflowStatus - Hook for calculating workflow section statuses
 *
 * Extracts status calculation logic from WorkflowStatusSidebar to improve
 * separation of concerns and testability.
 *
 * Status indicators are tri-state:
 * - Empty (gray): No data entered
 * - Partial (yellow): Some data entered, not calculated
 * - Complete (green): Data entered and calculated
 *
 * Usage:
 * ```tsx
 * const statuses = useWorkflowStatus(projectId);
 * // statuses.dates, statuses.tax, statuses.debt, etc.
 * ```
 */

import { useMemo } from 'react';

import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { convertToEngineFormat } from '~/builders/taxable-value/TaxableValueInputs';
import { useProjectStore } from '~/hooks/useProjectStore';

export type SectionStatus = 'empty' | 'partial' | 'complete';

export interface WorkflowStatuses {
  dates: SectionStatus;
  tax: SectionStatus;
  debt: SectionStatus;
  millage: SectionStatus;
  drf: SectionStatus;
}

/**
 * useWorkflowStatus Hook
 *
 * Calculates status for each workflow section based on:
 * - Input data presence
 * - Calculated data presence
 * - Project metadata (e.g., hasExistingDebt)
 */
export const useWorkflowStatus = (projectId: string): WorkflowStatuses => {
  // Get data from store - separate subscriptions
  const dateRecords = useProjectStore((s) => s.getDateRecords(projectId));
  const dateInputs = useProjectStore((s) => s.getDateInputsForm(projectId));
  const taxValueMap = useProjectStore((s) => s.getTaxableValueMap(projectId));
  const taxValueInputsForm = useProjectStore((s) => s.getTaxValueInputsForm(projectId));
  const existingDebtProjects = useProjectStore((s) => s.getExistingDebtProjects(projectId));
  const existingDebtInputsForm = useProjectStore((s) => s.getExistingDebtInputsForm(projectId));
  const millageInputs = useProjectStore((s) => s.getMillageInputsForm(projectId));
  const drfInputs = useProjectStore((s) => s.getDRFInputsForm(projectId));

  return useMemo(() => {
    return {
      dates: (() => {
        const hasInputs =
          !!dateInputs &&
          ((dateInputs.startingTaxYear?.trim()?.length ?? 0) > 0 ||
            (dateInputs.fiscalYearEnd?.trim()?.length ?? 0) > 0 ||
            (dateInputs.paymentDates?.length ?? 0) > 0);
        const hasCalculated = !!dateRecords;

        if (!hasInputs && !hasCalculated) return 'empty' as SectionStatus;

        // Check if form inputs are valid (complete) - this works even if dateRecords haven't been built yet
        if (dateInputs) {
          const validation = DateBuilder.validateInputs(dateInputs);
          if (validation.isValid) {
            return 'complete' as SectionStatus;
          }
        }

        // If we have calculated data, it's definitely complete
        if (hasCalculated) return 'complete' as SectionStatus;

        return 'partial' as SectionStatus;
      })(),
      tax: (() => {
        const hasInputs =
          !!taxValueInputsForm &&
          ((taxValueInputsForm.initialTaxableValue?.trim()?.length ?? 0) > 0 ||
            (taxValueInputsForm.startYearGrowthRate?.trim()?.length ?? 0) > 0 ||
            (taxValueInputsForm.fiveYearGrowthRate?.trim()?.length ?? 0) > 0 ||
            (taxValueInputsForm.twentyYearGrowthRate?.trim()?.length ?? 0) > 0);
        const hasCalculated = !!taxValueMap;

        if (!hasInputs && !hasCalculated) return 'empty' as SectionStatus;

        // Check if form inputs are valid (complete) - this works even if taxValueMap hasn't been built yet
        if (taxValueInputsForm) {
          try {
            const engineInputs = convertToEngineFormat(taxValueInputsForm);
            const validation = TaxableValueBuilder.validateInputs(engineInputs);
            if (validation.isValid) {
              return 'complete' as SectionStatus;
            }
            // If validation fails, return partial even if we have calculated data
            // (calculated data may be stale if inputs became invalid)
            return 'partial' as SectionStatus;
          } catch {
            // If conversion fails, check if we have calculated data as fallback
            // (but prefer showing partial if inputs exist but are invalid)
            if (hasCalculated && !hasInputs) {
              return 'complete' as SectionStatus;
            }
            return 'partial' as SectionStatus;
          }
        }

        // If we have calculated data but no form inputs, show complete (fallback)
        if (hasCalculated) return 'complete' as SectionStatus;

        return 'partial' as SectionStatus;
      })(),
      debt: (() => {
        // Check if we have any debt projects (inputs or calculated)
        const hasInputs = !!existingDebtInputsForm && existingDebtInputsForm.length > 0;
        const hasCalculated = !!existingDebtProjects && existingDebtProjects.length > 0;

        // Empty: No debt projects at all
        if (!hasInputs && !hasCalculated) return 'empty' as SectionStatus;

        // If no inputs, show partial (calculated data exists but inputs were cleared)
        if (!existingDebtInputsForm || existingDebtInputsForm.length === 0) {
          return 'partial' as SectionStatus;
        }

        // Check if ALL projects are complete (not just the ones that pass basic validation)
        // A project is complete if it has id, name, payments, and bond series is complete
        const allProjectsComplete = existingDebtInputsForm.every((project) => {
          // Basic validation: must have id, name, and payments
          if (
            !project.id?.trim() ||
            !project.name?.trim() ||
            !project.payments ||
            project.payments.length === 0
          ) {
            return false;
          }

          // Bond series validation: check if at least one of the last two principal payments > 0
          if (project.payments.length < 2) {
            return false;
          }

          const sortedPayments = [...project.payments].sort((a, b) =>
            a.paymentDate.localeCompare(b.paymentDate)
          );
          const lastTwoPayments = sortedPayments.slice(-2);

          // Complete if at least one has principal > 0
          return lastTwoPayments.some((payment) => {
            const principalStr = payment.principalPayment || '';
            const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;
            return principal > 0;
          });
        });

        // Complete: All projects are complete (all have id, name, payments, and bond series complete)
        if (allProjectsComplete) {
          return 'complete' as SectionStatus;
        }

        // Partial: Has projects but not all are complete
        return 'partial' as SectionStatus;
      })(),
      millage: (() => {
        const anyFilled =
          !!millageInputs &&
          ((millageInputs.peakMillage?.trim()?.length ?? 0) > 0 ||
            (millageInputs.peakMillageDuration?.trim()?.length ?? 0) > 0 ||
            (millageInputs.minimumMillage?.trim()?.length ?? 0) > 0 ||
            (millageInputs.minimumMillageDuration?.trim()?.length ?? 0) > 0);
        const allFilled =
          !!millageInputs &&
          (millageInputs.peakMillage?.trim()?.length ?? 0) > 0 &&
          (millageInputs.peakMillageDuration?.trim()?.length ?? 0) > 0 &&
          (millageInputs.minimumMillage?.trim()?.length ?? 0) > 0 &&
          (millageInputs.minimumMillageDuration?.trim()?.length ?? 0) > 0;
        if (!anyFilled) return 'empty' as SectionStatus;
        return allFilled ? ('complete' as SectionStatus) : ('partial' as SectionStatus);
      })(),
      drf: (() => {
        const anyFilled =
          !!drfInputs &&
          ((drfInputs.startingBalance?.trim()?.length ?? 0) > 0 ||
            (drfInputs.minimumBalance?.trim()?.length ?? 0) > 0 ||
            (drfInputs.maximumBalance?.trim()?.length ?? 0) > 0);
        const allFilled =
          !!drfInputs &&
          (drfInputs.startingBalance?.trim()?.length ?? 0) > 0 &&
          (drfInputs.minimumBalance?.trim()?.length ?? 0) > 0 &&
          (drfInputs.maximumBalance?.trim()?.length ?? 0) > 0;
        if (!anyFilled) return 'empty' as SectionStatus;
        return allFilled ? ('complete' as SectionStatus) : ('partial' as SectionStatus);
      })(),
    };
  }, [
    dateInputs,
    dateRecords,
    taxValueInputsForm,
    taxValueMap,
    existingDebtInputsForm,
    existingDebtProjects,
    millageInputs,
    drfInputs,
  ]);
};
